import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { NotificationFeed } from '@mlm/ui';
import { listNotifications, markAllNotificationsRead, markNotificationRead } from '../lib/api';
import { fmtDateTime } from '../lib/format';

/**
 * JM-EX9 dedicated page, re-wired in LMS Phase 2 to the shared `@mlm/ui` feed
 * (the creator and admin SPAs render the same component). Infinite-scrolls via
 * TanStack Query's cursor (`createdAt` of the last item on the page). Clicking
 * an unread row marks it read; the bell + page invalidate together so the
 * unread badge clears immediately. Rows carrying `metadata.path` navigate.
 */
export function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useInfiniteQuery({
    queryKey: ['notifications', 'list'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listNotifications({ limit: 25, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <NotificationFeed
      items={items}
      isLoading={q.isLoading}
      hasNextPage={!!q.hasNextPage}
      isFetchingNextPage={q.isFetchingNextPage}
      onLoadMore={() => q.fetchNextPage()}
      onMarkRead={(id) => markOne.mutate(id)}
      onMarkAll={() => markAll.mutate()}
      markAllPending={markAll.isPending}
      onOpen={(path) => navigate(path)}
      formatDate={fmtDateTime}
    />
  );
}
